import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLanguages } from '../../hooks/useLanguages';
import { SearchService } from '../../services/SearchService';

// Search page — mirrors mobile's SearchScreen (lib/screens/search/search_screen.dart),
// backed by SearchService (lib/services/search_service.dart): a manifest-level
// search across vocabulary topics, grammar chapters, stories, and quick-sheet
// sections for the current native/target language pair. A result opens the
// matching existing web page for its type (there's no per-result deep link
// yet, same as the Dart screen opening the type's screen root).

const TYPE_ICONS = {
  vocab: '📚',
  grammar: '📐',
  story: '📖',
  quicksheet: '📋',
};

const TYPE_ROUTES = {
  vocab: '/learn/vocabulary',
  grammar: '/learn/grammar',
  story: '/learn/stories',
  quicksheet: '/quick-sheet',
};

const DEBOUNCE_MS = 250;

export default function SearchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { languagePrefs } = useAuth();
  const { languages } = useLanguages();
  const langMap = useMemo(() => Object.fromEntries(languages.map((l) => [l.code, l])), [languages]);

  const nativeCode = languagePrefs?.nativeLanguage || '';
  const targetCode = languagePrefs?.targetLanguage || '';
  const targetLang = langMap[targetCode] || { code: targetCode, label: targetCode, dir: 'ltr' };

  const typeLabels = {
    vocab: t('tools.vocabulary', 'Vocabulary'),
    grammar: t('tools.grammar', 'Grammar'),
    story: t('stories.title', 'Short Stories'),
    quicksheet: t('nav.quickSheet', 'Quick Sheet'),
  };

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  function runSearch(value) {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    SearchService.search(nativeCode, targetCode, value)
      .then((found) => {
        if (requestIdRef.current !== requestId) return;
        setResults(found);
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) return;
        setResults([]);
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        setLoading(false);
      });
  }

  function handleChange(event) {
    const value = event.target.value;
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      requestIdRef.current += 1;
      setResults([]);
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => runSearch(value), DEBOUNCE_MS);
  }

  function openResult(result) {
    navigate(TYPE_ROUTES[result.type] || '/');
  }

  const trimmed = query.trim();

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <h1 className="mb-4 text-2xl font-black text-slate-900">{t('search.title', 'Search')}</h1>

        <input
          type="text"
          autoFocus
          value={query}
          onChange={handleChange}
          placeholder={t('search.placeholder', 'Search vocabulary, grammar, stories...')}
          className="mb-5 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 shadow-sm outline-none focus:border-indigo-400"
        />

        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-2xl bg-slate-100" />
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <span className="text-5xl">🔎</span>
            <p className="mt-3 text-sm text-slate-500">
              {trimmed
                ? t('search.noResults', 'No results found.')
                : t('search.desc', 'Find vocabulary, grammar lessons, stories, and quick-sheet topics.')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {results.map((result, index) => (
              <button
                key={`${result.type}-${index}-${result.label}`}
                type="button"
                onClick={() => openResult(result)}
                className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-left shadow-sm transition-colors hover:border-indigo-200 hover:bg-indigo-50/40"
              >
                <span className="text-xl shrink-0" aria-hidden="true">
                  {result.icon || TYPE_ICONS[result.type] || '🔎'}
                </span>
                <div className="min-w-0 flex-1">
                  <p dir={targetLang.dir || 'ltr'} className="truncate text-base font-bold text-slate-800">
                    {result.label}
                  </p>
                  <p className="mt-0.5 text-xs font-semibold text-slate-400">
                    {typeLabels[result.type] || result.type}
                  </p>
                </div>
                <span className="shrink-0 text-slate-300" aria-hidden="true">›</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
